import { setAuthToken } from '@/api/api'
import { Loader } from '@/components/atoms/Loader'
import { i18nHelpers } from '@/i18n/init'
import { paths } from '@/routes/paths'
import {
  clearLocalStorage,
  clearSessionStorage,
  makeUrlWithLanguage,
  renameObjKeysBy,
  snakeToCamelCase,
  useLanguage,
} from '@/utils'
import {
  Auth0Provider,
  useAuth0,
  LogoutOptions,
  RedirectLoginOptions,
} from '@auth0/auth0-react'
import { assoc, isEmpty, omit, pipe } from 'ramda'
import { useEffect, useState, ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { config } from '../config'

interface User {
  [key: string]: any
  'https://surein.auth0/role'?: string[]
}

class FailedToGetAccessToken extends Error {
  name = 'FailedToGetAccessToken'
  cause: unknown

  constructor(cause: unknown) {
    super('Failed to get access token')
    this.cause = cause
  }
}

const makeRedirectURL = makeUrlWithLanguage(paths.authRedirect)

interface TokenProviderProps {
  children: ReactNode
}

const TokenProvider = ({ children }: TokenProviderProps) => {
  const {
    isAuthenticated,
    getAccessTokenSilently,
    getAccessTokenWithPopup,
  } = useAuth0()
  const [isLoading, setLoading] = useState(true)

  useEffect(() => {
    if (isAuthenticated) {
      setLoading(true)
      const payload = {
        audience: config.backendApiAudience,
        scope: 'openid profile email',
      }

      const getToken = window.origin.includes('localhost')
        ? getAccessTokenWithPopup
        : getAccessTokenSilently

      getToken(payload)
        .then((accessToken) => {
          setAuthToken(accessToken)
          setLoading(false)
        })
        .catch((err) => {
          setLoading(false)
          throw new FailedToGetAccessToken(err)
        })
    }
  }, [isAuthenticated, getAccessTokenWithPopup, getAccessTokenSilently])

  if (isLoading && isAuthenticated) return <Loader />

  return <>{children}</>
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const { i18n } = useTranslation()

  return (
    <Auth0Provider
      clientId={config.auth0ClientId}
      domain={config.authDomain}
      redirectUri={
        makeRedirectURL(i18n.language || i18nHelpers.fallbackLng) as string
      }
    >
      <TokenProvider>{children}</TokenProvider>
    </Auth0Provider>
  )
}

const assignRole = (user: User = {}) => {
  const [role] = user?.['https://surein.auth0/role'] || []

  if (isEmpty(user)) return undefined

  return pipe(omit(['https://surein.auth0/role']), assoc('role', role))(user)
}

const transform = renameObjKeysBy(snakeToCamelCase)

export const useAuth = () => {
  const authContext = useAuth0()
  const language = useLanguage()

  const login = (returnTo?: string, options?: RedirectLoginOptions) => {
    authContext.loginWithRedirect({
      ...options,
      redirectUri: returnTo || makeRedirectURL(language),
      ui_locales: language,
    })
  }

  const logout = (returnTo?: string, options?: LogoutOptions) => {
    clearSessionStorage()
    clearLocalStorage()
    authContext.logout({
      ...options,
      returnTo: returnTo || makeRedirectURL(language),
    })
  }

  const user = assignRole(authContext.user)

  return transform({
    ...authContext,
    login,
    logout,
    user,
  })
}
